import { useQuery } from '@tanstack/react-query';
import { fetchAutocompleteOptions } from './autocomplete-request';
import type { PostingRequirementsDef } from './requirements-key';
import type { VonqPostingRequirementField as FieldDef } from 'api-clients/monolith';
import { useEffect, useRef, useState } from 'react';
import { Control, FieldPath, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { FormSelect } from '@/components/ui/select';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { queryKeys } from '@/query-keys';
import { stripVonq } from '@/utils/strip-vonq';

import { JobDetailFormState, JobPostingForm } from '../../types';
import { parseTermSources, resolveTermValue } from './dependent-autocomplete';
import { mapOptions } from './option-helpers';

interface VonqDependentAutocompleteInputProps {
  field: FieldDef;
  def: PostingRequirementsDef;
  openingId: string;
  name: FieldPath<JobPostingForm>;
  control: Control<JobPostingForm>;
  errorMessage: string | undefined;
  isMultiSelect?: boolean;
}

/**
 * Handles VONQ Pattern 3 (dependent facet) and Pattern 4 (vacancy field)
 * autocomplete fields.
 *
 * The facet's options can only be fetched once the values it depends on are
 * available — either sibling facets in the same contract (Pattern 3) and/or a
 * field from the job/opening such as the title (Pattern 4). We watch those
 * values, build the `term` payload (a single string for one dependency, an
 * ordered array for several), and:
 *   - keep the input disabled until every dependency is resolved;
 *   - fetch and render options (searchable, client-side filtered) once resolved;
 *   - clear the current selection and re-fetch whenever a dependency changes.
 *
 * `credentials` in `required_parameters` is handled server-side by the contract
 * endpoint, so no client work is needed for it.
 */
export function VonqDependentAutocompleteInput({
  field,
  def,
  openingId,
  name,
  control,
  errorMessage,
  isMultiSelect = false,
}: VonqDependentAutocompleteInputProps) {
  const intl = useIntl();
  const { hookForm } = useJobPostingContext();
  const isHier = field.type === 'HIER';

  const sources = parseTermSources(field.autocomplete);
  const hasFacetSource = sources.some(source => source.kind === 'facet');

  // Watch the whole opening object — it carries both the vacancy fields (title,
  // …) and this contract's facet values (under vonqPostingRequirements).
  const openingValues = useWatch({
    control,
    name: `jobDetails.${openingId}` as FieldPath<JobPostingForm>,
  }) as Partial<JobDetailFormState> | undefined;

  const contractValues =
    (openingValues?.vonqPostingRequirements?.[def.key] as
      | Record<string, string | string[]>
      | undefined) ?? {};

  const term = resolveTermValue(sources, contractValues, openingValues ?? {});
  const isResolved = term !== null;

  const [query, setQuery] = useState('');

  const { data, isFetching } = useQuery({
    queryKey: queryKeys.jobPosting.vonqAutocomplete.dependent(
      def.key,
      field.name,
      term ?? '',
    ),
    queryFn: async () => {
      // Some facets 400 on particular terms — surface an empty list rather than
      // crashing (mirrors the lazy-load branch).
      try {
        return await fetchAutocompleteOptions(def, field.name, { term });
      } catch {
        return { options: [] };
      }
    },
    enabled: isResolved,
    staleTime: 60_000,
  });

  // Reset the child value whenever the resolved term changes (a parent facet or
  // the vacancy field changed) so a stale selection can't survive. Seeded at
  // mount so the initial render never clears an already-populated value.
  const termKey = term === null ? null : JSON.stringify(term);
  const prevTermKeyRef = useRef<string | null>(termKey);
  const isInitialRef = useRef(true);
  useEffect(() => {
    if (isInitialRef.current) {
      isInitialRef.current = false;
      prevTermKeyRef.current = termKey;
      return;
    }
    if (termKey !== prevTermKeyRef.current) {
      prevTermKeyRef.current = termKey;
      hookForm.setValue(name, (isMultiSelect ? [] : '') as never, {
        shouldValidate: true,
      });
      setQuery('');
    }
  }, [termKey, name, isMultiSelect, hookForm]);

  const options = mapOptions(data?.options, isHier);
  const filteredOptions = query
    ? options.filter(opt =>
        opt.label.toLowerCase().includes(query.toLowerCase()),
      )
    : options;

  const disabledPlaceholder = hasFacetSource
    ? intl.formatMessage({
        defaultMessage: 'Fill in the required fields above first',
        description:
          'Placeholder for a job-board field whose selectable options only load after the fields it depends on are filled',
      })
    : intl.formatMessage({
        defaultMessage: 'Add a job title first',
        description:
          'Placeholder for a job-board field whose options depend on the job title, shown before a title has been entered',
      });

  return (
    <FormSelect
      name={name}
      control={control}
      label={stripVonq(field.label)}
      required={field.required}
      error={errorMessage}
      isMultiSelect={isMultiSelect}
      options={isResolved ? filteredOptions : []}
      isLoading={isResolved && isFetching}
      isDisabled={!isResolved}
      query={query}
      setQuery={q => setQuery(q ?? '')}
      isClearable
      placeholder={
        isResolved
          ? intl.formatMessage({
              defaultMessage: 'Select an option',
              description:
                'Placeholder for a job-board dropdown once its dependent options have loaded',
            })
          : disabledPlaceholder
      }
    />
  );
}
